import {createI18nScope, ref} from 'common/i18n/scope';
import {withComment, withDetail} from 'common/i18n/util';
import mixpanelSettingsScope from 'common/mixpanel-settings/index.i18n';
import savedEntitiesScope from 'common/report/util/saved-entities/index.i18n';

const __i18nScope = createI18nScope(`routes.global-sidenav`, {
  'common.alpha': `Alpha`,
  'common.beta': `Beta`,
  'common.new': `New`,
  'common.search': `Search`,
  'copilot.mixpanel-agent': `Mixpanel Agent`,
  'experiments.list.limit-reached': `You've used all free experiments included in your plan — upgrade to create more.`,
  'feature-flags.create.limit-exceeded': `You've used all {{limit}} feature flags included in your plan — upgrade to create more.`,
  'feature-flags.create.trial-limit-exceeded': `You've used all free feature flags included in your plan — upgrade to create more.`,
  'global-sidenav.collapse-side-nav': `Collapse Side Nav`,
  'global-sidenav.create-new': `Create New`,
  'global-sidenav.create-new-dropdown.analysis': `ANALYSIS`,
  'global-sidenav.create-new-dropdown.experimentation': `EXPERIMENTATION`,
  'global-sidenav.create-new-dropdown.report': `REPORT`,
  'global-sidenav.create-with-agent.menu-label': `Mixpanel Agent`,
  'global-sidenav.show-side-nav': `Show Side Nav`,
  'mixology.agent-flow': ref(savedEntitiesScope, `mixology.agent-flow`),
  'mixology.agent-flow_plural': ref(savedEntitiesScope, `mixology.agent-flow_plural`),
  'mixology.app.agent-flows': `Automations`,
  'mixology.app.boards': `${ref(savedEntitiesScope, `mixology.board_plural`)}`,
  'mixology.app.canvases': `${ref(savedEntitiesScope, `mixology.metric-tree_plural`)}`,
  'mixology.app.cohorts': `${ref(savedEntitiesScope, `mixology.cohort_plural`)}`,
  'mixology.app.data': withDetail(`Data`, {excludeFromMixologyDict: true}),
  'mixology.app.data-audit': `Data Audit`,
  'mixology.app.destinations': `Destinations`,
  'mixology.app.events': `${ref(savedEntitiesScope, `mixology.event_plural`)}`,
  'mixology.app.experimentation': `Experimentation`,
  'mixology.app.experiments': withComment(`e.g. "A/B Experiments"`, `Experiments`),
  'mixology.app.experiments-legacy': withComment(`e.g. "A/B Experiments"`, `Experiments`),
  'mixology.app.feature-flags': `Feature Flags`,
  'mixology.app.flows': withComment(`e.g. "User Flows"`, `Flows`),
  'mixology.app.funnels': withComment(`e.g. "User Conversion Funnels"`, `Funnels`),
  'mixology.app.heat-map-collections': `Heatmap Collections`,
  'mixology.app.heat-maps': `Heatmaps`,
  'mixology.app.home': `Home`,
  'mixology.app.impact': withComment(`e.g. "Impact Analysis"`, `Impact`),
  'mixology.app.insights': withComment(
    `e.g. "Product Insights" - analyses that reveal what users experience when interacting with a product`,
    `Insights`,
  ),
  'mixology.app.integrations': `Integrations`,
  'mixology.app.jql': `JQL`,
  'mixology.app.lexicon': withComment(
    `defined set of terms, vocabulary, or dictionary that is used to describe data, events, or metrics within a specific domain`,
    `Lexicon`,
  ),
  'mixology.app.metric-trees': `${ref(savedEntitiesScope, `mixology.metric-tree_plural`)}`,
  'mixology.app.playlists': `Playlists`,
  'mixology.app.retention': withComment(`e.g. "User Retention"`, `Retention`),
  'mixology.app.session_replay': `Session Replay`,
  'mixology.app.signal': withComment(
    `e.g. "User Signals" - referring to the data or indicators that users generate while interacting with a product`,
    `Signal`,
  ),
  'mixology.app.users': `${ref(mixpanelSettingsScope, `mixology.user_plural`)}`,
  'mixology.board': ref(savedEntitiesScope, `mixology.board`),
  'mixology.board_plural': ref(savedEntitiesScope, `mixology.board_plural`),
  'mixology.experiment': ref(savedEntitiesScope, `mixology.experiment`),
  'mixology.experiment_plural': ref(savedEntitiesScope, `mixology.experiment_plural`),
  'mixology.feature-flag': ref(savedEntitiesScope, `mixology.feature-flag`),
  'mixology.feature-flag_plural': ref(savedEntitiesScope, `mixology.feature-flag_plural`),
  'mixology.heat-map': ref(savedEntitiesScope, `mixology.heat-map`),
  'mixology.metric-tree': ref(savedEntitiesScope, `mixology.metric-tree`),
  'mixology.metric-tree_plural': ref(savedEntitiesScope, `mixology.metric-tree_plural`),
  'mixology.playlist': ref(savedEntitiesScope, `mixology.playlist`),
  'sidenav.agent-analytics': `Agent Intelligence`,
  'sidenav.agent-analytics-conversations': `Conversations`,
  'sidenav.agent-analytics-usage': `Usage`,
  'sidenav.descriptions.agent': `Ask a question about your data and get an answer`,
  'sidenav.descriptions.agent-analytics': `Measure how your AI agents perform`,
  'sidenav.descriptions.agent-analytics-conversations': `Step through any agent conversation, turn by turn`,
  'sidenav.descriptions.agent-analytics-usage': `Track agent usage, cost, latency, and errors over time`,
  'sidenav.descriptions.create-new': `Start a new board, report, experiment, etc.`,
  'sidenav.descriptions.data': `Manage the events, users, and properties in your project`,
  'sidenav.descriptions.destinations': `Send your Mixpanel data to your other tools`,
  'sidenav.descriptions.events': `Browse the events flowing into your project`,
  'sidenav.descriptions.experiments': `Test a change and measure its impact`,
  'sidenav.descriptions.feature-flags': `Roll out a feature to specific users, then measure it`,
  'sidenav.descriptions.home': `Your recent boards, reports, and project activity`,
  'sidenav.descriptions.lexicon': `Name, describe, and organize your events and properties`,
  'sidenav.descriptions.search': `Find any board, report, event, or user`,
  'sidenav.descriptions.session-replay': `Watch how real users move through your product`,
  'sidenav.descriptions.users': `Look up a user profile and everything they have done`,
  'sidenav.discover': `Discover`,
  'sidenav.experiments': `Experiments`,
  'sidenav.feature-flags': `Feature Flags`,
  'text-analysis.text-analysis': `Text Analysis`,
});

__i18nScope.registerTranslationLoaders({"es": () => import("virtual:i18n-scope/es/routes.global-sidenav"), "fr": () => import("virtual:i18n-scope/fr/routes.global-sidenav"), "id": () => import("virtual:i18n-scope/id/routes.global-sidenav"), "ja": () => import("virtual:i18n-scope/ja/routes.global-sidenav"), "ko": () => import("virtual:i18n-scope/ko/routes.global-sidenav"), "pt": () => import("virtual:i18n-scope/pt/routes.global-sidenav"), "zh-CN": () => import("virtual:i18n-scope/zh-CN/routes.global-sidenav"), "zh-TW": () => import("virtual:i18n-scope/zh-TW/routes.global-sidenav")});
export default __i18nScope;
